import dayjs from "dayjs";

export const getWeekDate = ({ date, selectedWeek }: { date?: Date, selectedWeek: number }) => {
  const weekFormat = 'YYYY-MM-DDTHH:mm:ssZ';
  const currentDate = date instanceof Date ? date : new Date();
  
  // Configure dayjs to use Monday as first day of week
  dayjs.locale('en', {
      weekStart: 1 
  });
  
  // Get the current week number (with Monday as first day)
  const januaryFirst = new Date(currentDate.getFullYear(), 0, 1);
  const daysToFirstMonday = (januaryFirst.getDay() === 0) ? 1 : (januaryFirst.getDay() === 1) ? 0 : (8 - januaryFirst.getDay());
  const firstMonday = new Date(currentDate.getFullYear(), 0, januaryFirst.getDate() + daysToFirstMonday);
  
  // Calculate current week number (1-53)
  let week = 1;
  if (currentDate >= firstMonday) {
      const daysDiff = Math.floor((currentDate.getTime() - firstMonday.getTime()) / (24 * 60 * 60 * 1000));
      week = Math.floor(daysDiff / 7) + 1;
  } else {
      // Handle dates before first Monday of the year (use last week of previous year)
      const lastYear = new Date(currentDate.getFullYear() - 1, 11, 31);
      const lastYearWeek = getWeekDate({ date: lastYear, selectedWeek: 1 }).week;
      week = lastYearWeek;
  }
  
  // Calculate the target date based on selected week
  let targetDate = new Date(currentDate);
  if (selectedWeek !== week) {
      const weekDiff = selectedWeek - week;
      targetDate = new Date(currentDate.getTime() + weekDiff * 7 * 24 * 60 * 60 * 1000);
  }
  
  // Get start and end of week
  const dayOfWeek = targetDate.getDay(); 
  const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  
  // Calculate Monday (start of week)
  const monday = new Date(targetDate);
  monday.setDate(targetDate.getDate() - daysFromMonday);
  monday.setHours(0, 0, 0, 0);
  
  // Calculate Sunday (end of week)
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);
  
  return {
      week,
      startOfWeek: dayjs(monday).format(weekFormat),
      endOfWeek: dayjs(sunday).format(weekFormat)
  };
};

export const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric', 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true 
    };
  
    return date.toLocaleString('en-US', options).replace(',', ' -');
  };

  export const formatDateFromEpoch=(epoch:number)=>{
    if(!epoch){
      return ''
    }
    const date = new Date(epoch);
    return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
  }
  export const formatTimeFromEpoch=(epoch:number)=> {
    if(!epoch){
      return ''
    }
    const date = new Date(epoch);
    let hours = date.getHours() % 12 || 12;
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = date.getHours() >= 12 ? 'pm' : 'am';
  
    return `${hours}:${minutes}${ampm}`;
  }
export const relativeTime = (loginTime: string | number): string => {
    const msPerMinute = 60 * 1000;
    const msPerHour = msPerMinute * 60;
    const msPerDay = msPerHour * 24;
    const msPerMonth = msPerDay * 30;
    const msPerYear = msPerDay * 365;

    const loginTimeMilliseconds = typeof loginTime === 'string' ? new Date(loginTime).getTime() : loginTime * 1000;
    const elapsed = Date.now() - loginTimeMilliseconds;

    if (elapsed < msPerMinute) {
        const seconds = Math.round(elapsed / 1000);
        return seconds === 1 ? '1 second ago' : `${seconds} seconds ago`;
    } else if (elapsed < msPerHour) {
        const minutes = Math.round(elapsed / msPerMinute);
        return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`;
    } else if (elapsed < msPerDay) {
        const hours = Math.round(elapsed / msPerHour);
        return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
    } else if (elapsed < msPerMonth) {
        const days = Math.round(elapsed / msPerDay);
        return days === 1 ? '1 day ago' : `${days} days ago`;
    } else if (elapsed < msPerYear) {
        const months = Math.round(elapsed / msPerMonth);
        return months === 1 ? '1 month ago' : `${months} months ago`;
    } else {
        const years = Math.round(elapsed / msPerYear);
        return years === 1 ? '1 year ago' : `${years} years ago`;
    }
};

export const taskDuration=(startTime: string, endTime: string): string=>{
  const start = new Date(startTime);
  const end = new Date(endTime);
  const diffInMs = Math.abs(end.getTime() - start.getTime());

  // Convert milliseconds to hours and minutes
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const hours = Math.floor(diffInMinutes / 60);
  const minutes = diffInMinutes % 60;

  return `${hours} hour${hours !== 1 ? 's' : ''} and ${minutes} minute${minutes !== 1 ? 's' : ''}`;
}